import { useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Paperclip, Send, Loader2, X } from "lucide-react";
import { submitFeedback } from "@/lib/api";

const FEEDBACK_MAILBOX = "contactus@hipo.co.in";
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const categories = [
  { value: "bug", label: "Bug" },
  { value: "suggestion", label: "Suggestion" },
  { value: "uiux", label: "UI/UX" },
  { value: "other", label: "Other" },
] as const;

type CategoryValue = (typeof categories)[number]["value"];

export function FeedbackForm() {
  const { toast } = useToast();
  const { user } = useUser();
  const [category, setCategory] = useState<CategoryValue>("bug");
  const [description, setDescription] = useState("");
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = description.trim().length >= 10 && !isSubmitting;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (file && file.size > MAX_FILE_SIZE) {
      toast({
        title: "File too large",
        description: "Maximum file size is 2MB. Please choose a smaller image.",
        variant: "destructive",
      });
      event.target.value = "";
      setScreenshotFile(null);
      setPreviewUrl(null);
      return;
    }
    
    setScreenshotFile(file);
    
    // Create preview URL for image files
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleRemoveFile = () => {
    setScreenshotFile(null);
    
    // Clean up preview URL to prevent memory leak
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    
    // Reset the file input
    const fileInput = document.getElementById('screenshot') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) {
      toast({
        title: "Add a bit more detail",
        description: "Please share at least a couple of sentences so we can help faster.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await submitFeedback({
        category,
        description: description.trim(),
        screenshot: screenshotFile || undefined,
        userEmail: user?.primaryEmailAddress?.emailAddress || undefined,
      });

      toast({
        title: "Feedback submitted successfully!",
        description: "Thank you for helping us improve HiPo AI Coach. We'll review your feedback soon.",
      });

      // Reset form
      setCategory("bug");
      setDescription("");
      setScreenshotFile(null);
      
      // Clean up preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }

    } catch (error: any) {
      console.error('Failed to submit feedback:', error);

      // Fallback to mailto if API fails
      const subject = encodeURIComponent(`[Feedback] ${category.toUpperCase()} report`);
      const bodyLines = [
        `Issue Category: ${categories.find((item) => item.value === category)?.label ?? category}`,
        "",
        "Description:",
        description.trim(),
        "",
      ];

      if (screenshotFile) {
        bodyLines.push(`Attachment: ${screenshotFile.name} (remember to attach before sending)`);
        bodyLines.push("");
      }

      bodyLines.push("—", "Submitted from HiPo AI Coach Feedback portal");

      const mailtoHref = `mailto:${FEEDBACK_MAILBOX}?subject=${subject}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
      window.location.href = mailtoHref;

      toast({
        title: "Using email fallback",
        description: "Opening your email client as backup. Review the draft and hit send.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="border-border/80 shadow-xl backdrop-blur supports-[backdrop-filter]:bg-white/70">
      <CardHeader className="space-y-2">
        <CardTitle className="font-display text-2xl text-slate-900">Report an Issue or Share Feedback</CardTitle>
        <CardDescription className="text-sm text-slate-600">
          Your feedback helps us polish the HiPo AI Coach experience. Tell us what's working, what's not, and what you'd love to see next.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="category">Issue category</Label>
            <div className="relative">
              <select
                id="category"
                value={category}
                onChange={(event) => setCategory(event.target.value as CategoryValue)}
                aria-label="Issue category"
                className="w-full appearance-none rounded-md border border-border bg-white/80 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {categories.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground">
                ▾
              </span>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Tell us what happened, what you expected, or how we can make things better."
              minLength={10}
              rows={6}
              className="resize-none bg-white/80"
              required
            />
            <p className="text-xs text-muted-foreground">Try to include steps to reproduce, error messages, or ideas for improvement.</p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="screenshot">Screenshot / attachment (optional, max 2MB)</Label>
            <div className="flex items-center gap-3 rounded-lg border border-dashed border-border/60 bg-white/70 px-4 py-3">
              <Paperclip className="h-4 w-4 text-muted-foreground" aria-hidden />
              <Input
                id="screenshot"
                type="file"
                accept="image/*"
                className="cursor-pointer border-0 bg-transparent p-0 file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-primary-foreground hover:file:bg-primary/90"
                onChange={handleFileChange}
              />
            </div>
            {screenshotFile && (
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <p className="text-xs text-emerald-700">
                    ✓ {screenshotFile.name} ({(screenshotFile.size / 1024).toFixed(0)} KB)
                  </p>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="ml-2 rounded-full p-1 text-emerald-600 hover:bg-emerald-200 hover:text-emerald-800 transition-colors"
                    aria-label="Remove attachment"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                
                {/* Image Preview */}
                {previewUrl && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs text-gray-600 mb-2">Preview:</p>
                    <img
                      src={previewUrl}
                      alt="Attachment preview"
                      className="max-w-full max-h-48 rounded-md border border-gray-200 object-contain"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">Need quick help? Email us directly at <a className="font-medium text-primary hover:underline" href={`mailto:${FEEDBACK_MAILBOX}`}>{FEEDBACK_MAILBOX}</a>.</p>
            <Button type="submit" size="lg" className="inline-flex items-center gap-2 !text-white" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isSubmitting ? 'Submitting...' : 'Submit feedback'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
